/**
 * Credit Notes — single place for Accounts to see ALL credit notes (client
 * refunds + vendor credits) with a direction filter, plus the full
 * Requested → Approved → Processed → Completed workflow and PDF download.
 * Reuses generatePDF() (same Playwright pipeline as invoices/vouchers).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Download, CheckCircle2, Trash2, Loader2, Undo2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/utils';
import { generatePDF } from '../utils/generatePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_REASONS = [
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'overpayment_correction', label: 'Overpayment Correction' },
  { value: 'service_issue', label: 'Service Issue' },
  { value: 'other', label: 'Other' },
];
const VENDOR_REASONS = [
  { value: 'vendor_cancellation', label: 'Vendor Cancellation' },
  { value: 'service_not_delivered', label: 'Service Not Delivered' },
  { value: 'overbilling_correction', label: 'Overbilling Correction' },
  { value: 'other', label: 'Other' },
];
const REASON_LABEL = Object.fromEntries([...CLIENT_REASONS, ...VENDOR_REASONS].map((r) => [r.value, r.label]));
const CLIENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'original_payment_reversal', label: 'Original Payment Reversal' },
  { value: 'other', label: 'Other' },
];

const STATUS_PILL = {
  requested: 'bg-slate-100 text-slate-600', approved: 'bg-blue-50 text-blue-700',
  processed: 'bg-amber-50 text-amber-700', completed: 'bg-emerald-50 text-emerald-700',
};
const DIRECTION_LABEL = { client_refund: 'Client Refund', vendor_credit: 'Vendor Credit' };

const EMPTY_FORM = {
  direction: 'client_refund', amount: '', reason: 'cancellation', reasonDetail: '', method: 'bank_transfer', notes: '',
  tripId: '', invoiceNumber: '', vendorId: '', bookingRef: '', applyToAdvanceBalance: false,
};

const CreditNotes = () => {
  const { user } = useAuth();
  const canApprove = user?.role === 'admin' || user?.role === 'accounts';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${BACKEND_URL}/api/credit-notes`, { params });
      setRows(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load credit notes'));
    } finally { setLoading(false); }
  }, [directionFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        direction: form.direction, amount: Number(form.amount) || 0,
        reason: form.reason, reasonDetail: form.reasonDetail, notes: form.notes,
      };
      if (form.direction === 'client_refund') {
        Object.assign(payload, { method: form.method, tripId: form.tripId.trim(), invoiceNumber: form.invoiceNumber.trim() });
      } else {
        Object.assign(payload, { vendorId: form.vendorId.trim(), bookingRef: form.bookingRef.trim(), applyToAdvanceBalance: form.applyToAdvanceBalance });
      }
      await axios.post(`${BACKEND_URL}/api/credit-notes`, payload);
      toast.success('Credit note requested');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create credit note'));
    } finally { setSaving(false); }
  };

  const transition = async (id, action) => {
    setBusyId(id);
    try {
      await axios.put(`${BACKEND_URL}/api/credit-notes/${id}/${action}`, {});
      toast.success(`Credit note ${action}d`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, `Failed to ${action} credit note`));
    } finally { setBusyId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this credit note request?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/credit-notes/${id}`);
      toast.success('Credit note deleted');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete credit note'));
    } finally { setBusyId(null); }
  };

  const handleDownload = async (row) => {
    setDownloadingId(row.creditNoteId);
    try {
      await generatePDF({
        pageUrl: `${window.location.origin}/credit-note-preview/${row.creditNoteId}`,
        filename: `${row.creditNoteNumber || row.creditNoteId}-CreditNote.pdf`,
        waitUntil: 'load',
        waitSelector: '[data-testid="credit-note-preview-page"]',
      });
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF generation failed');
    } finally { setDownloadingId(null); }
  };

  const reasonOptions = form.direction === 'client_refund' ? CLIENT_REASONS : VENDOR_REASONS;

  return (
    <div className="space-y-4" data-testid="credit-notes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Credit Notes</h1>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowCreate(true); }} data-testid="new-credit-note-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
          <Plus className="h-4 w-4 mr-2" />New Credit Note
        </Button>
      </div>

      <div className="flex gap-2">
        <Select value={directionFilter} onValueChange={setDirectionFilter}>
          <SelectTrigger className="w-52" data-testid="direction-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Directions</SelectItem>
            <SelectItem value="client_refund">Client Refunds</SelectItem>
            <SelectItem value="vendor_credit">Vendor Credits</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="requested">Requested</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="processed">Processed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="credit-notes-empty-state">
          <Undo2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No credit notes yet.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
          {rows.map((r) => (
            <div key={r.creditNoteId} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`credit-note-row-${r.creditNoteId}`}>
              <div className="min-w-[220px]">
                <p className="text-sm font-semibold text-slate-900">
                  {r.creditNoteNumber || <span className="text-slate-400 italic">Not yet numbered</span>}
                  <span className="ml-2 text-xs font-normal text-slate-500">{DIRECTION_LABEL[r.direction]}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {r.direction === 'client_refund' ? (r.clientName || r.tripId) : r.vendorName} · ₹{Number(r.amount).toLocaleString('en-IN')} · {REASON_LABEL[r.reason] || r.reason}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[r.status]}`} data-testid={`credit-note-status-${r.creditNoteId}`}>
                {r.status.toUpperCase()}
              </span>
              <div className="flex items-center gap-1.5">
                {canApprove && r.status === 'requested' && (
                  <Button size="sm" variant="outline" disabled={busyId === r.creditNoteId} onClick={() => transition(r.creditNoteId, 'approve')} data-testid={`approve-btn-${r.creditNoteId}`}>
                    {busyId === r.creditNoteId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Approve'}
                  </Button>
                )}
                {canApprove && r.status === 'approved' && (
                  <Button size="sm" variant="outline" disabled={busyId === r.creditNoteId} onClick={() => transition(r.creditNoteId, 'process')} data-testid={`process-btn-${r.creditNoteId}`}>
                    {busyId === r.creditNoteId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Process'}
                  </Button>
                )}
                {canApprove && r.status === 'processed' && (
                  <Button size="sm" variant="outline" disabled={busyId === r.creditNoteId} onClick={() => transition(r.creditNoteId, 'complete')} data-testid={`complete-btn-${r.creditNoteId}`}>
                    {busyId === r.creditNoteId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Complete</>}
                  </Button>
                )}
                {canApprove && (r.status === 'processed' || r.status === 'completed') && (
                  <Button size="sm" variant="outline" disabled={downloadingId === r.creditNoteId} onClick={() => handleDownload(r)} data-testid={`download-btn-${r.creditNoteId}`}>
                    {downloadingId === r.creditNoteId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  </Button>
                )}
                {canApprove && r.status === 'requested' && (
                  <Button size="sm" variant="ghost" disabled={busyId === r.creditNoteId} onClick={() => handleDelete(r.creditNoteId)} data-testid={`delete-btn-${r.creditNoteId}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-credit-note-dialog">
          <DialogHeader><DialogTitle>New Credit Note</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...EMPTY_FORM, direction: v })} disabled={user?.role === 'agent'}>
                <SelectTrigger className="mt-1" data-testid="direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_refund">Client Refund</SelectItem>
                  <SelectItem value="vendor_credit" disabled={user?.role === 'agent'}>Vendor Credit (Admin/Accounts only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.direction === 'client_refund' ? (
              <>
                <div>
                  <Label>Trip ID</Label>
                  <Input value={form.tripId} onChange={(e) => set('tripId', e.target.value)} placeholder="TRV_TRIP_0826_0037 or TRV000766" className="mt-1" data-testid="refund-trip-id-input" required />
                </div>
                <div>
                  <Label>Invoice Number (optional)</Label>
                  <Input value={form.invoiceNumber} onChange={(e) => set('invoiceNumber', e.target.value)} className="mt-1" data-testid="refund-invoice-number-input" />
                </div>
                <div>
                  <Label>Refund Method</Label>
                  <Select value={form.method} onValueChange={(v) => set('method', v)}>
                    <SelectTrigger className="mt-1" data-testid="refund-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLIENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Vendor ID</Label>
                  <Input value={form.vendorId} onChange={(e) => set('vendorId', e.target.value)} placeholder="VND_0926_XXXX" className="mt-1" data-testid="credit-vendor-id-input" required />
                </div>
                <div>
                  <Label>Booking / Trip Reference (optional)</Label>
                  <Input value={form.bookingRef} onChange={(e) => set('bookingRef', e.target.value)} className="mt-1" data-testid="credit-booking-ref-input" />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.applyToAdvanceBalance} onChange={(e) => set('applyToAdvanceBalance', e.target.checked)} data-testid="apply-advance-balance-checkbox" />
                  Apply to vendor's advance balance (if one exists)
                </label>
              </>
            )}
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} className="mt-1" data-testid="credit-note-amount-input" required />
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => set('reason', v)}>
                <SelectTrigger className="mt-1" data-testid="credit-note-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{reasonOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Detail (optional)</Label>
              <Textarea value={form.reasonDetail} onChange={(e) => set('reasonDetail', e.target.value)} className="mt-1" rows={2} data-testid="credit-note-reason-detail-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} data-testid="cancel-create-credit-note-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="submit-credit-note-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {saving ? 'Requesting…' : 'Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotes;
